import { useState } from "react";
import { useNavigate } from "react-router-dom";

// HOOKS
import useScreenSize from "../../../../hooks/useScreenSize";

// REDUX
import { useDispatch } from "react-redux";
import {
  updateCryptoSelectedAccount,
  updateCryptoStep,
} from "../../../../store/slices/cryptoSlice";

// CONSTANTS
import { MOBILE_OPERATE_WIDTH } from "../../../OperateMEP/constants";

// TYPES
import { CryptoAccount } from "../../types";

// COMPONENTS
import { Chip, ModalBody, ModalHeader, Tooltip } from "@nextui-org/react";
import { ButtonsFooter } from "../ButtonsFooter/ButtonsFooter";
import AccountCard from "../../../UnifiedBalance/ui/AccountCard/AccountCard";
import Spinner from "../../../Spinner/Spinner";
import { BackHeader } from "../BackHeader/BackHeader";

// STYLES
import styles from "./AccountsModal.module.scss";

// ASSETS
import InfoIcon from "../../../../assets//images/icons/ui/states/alert-soft-blue-icon.svg?react";

interface Props {
  onClose: () => void;
  companyName: string;
  accountOptions: CryptoAccount[];
  isCryptoOnly: boolean;
}

const AccountsModal = ({
  companyName,
  accountOptions,
  onClose,
  isCryptoOnly,
}: Props) => {
  const {
    header,
    description,
    scrollContent,
    flexContainer,
    support,
    spinnerContainer,
    chipContent,
    infoIcon,
    tooltipContent,
    tooltipContainer,
  } = styles;
  const [selectedAccount, setSelectedAccount] = useState<CryptoAccount | null>(
    null,
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobile } = useScreenSize(MOBILE_OPERATE_WIDTH);

  const getCustomSideComponent = (account: CryptoAccount) => {
    if (account.acceptedTerms) return;
    if (account.isTitular && !account.isJointSignature) return;
    return (
      <div className="flex items-center gap-2 pl-[0.5rem]">
        <Chip
          radius="full"
          classNames={{ base: "bg-[#506E92]", content: chipContent }}
        >
          {!account.isTitular
            ? "No tenés permisos"
            : account.isJointSignature
              ? "Firma conjunta"
              : ""}
        </Chip>
        <Tooltip
          showArrow={true}
          classNames={{
            content: tooltipContent,
          }}
          color="foreground"
          placement="top"
          content={
            <div className={tooltipContainer}>
              <div>
                {!account.isTitular ? (
                  `Solo los titulares pueden crear una cuenta en ${companyName}`
                ) : account.isJointSignature ? (
                  <>
                    Al tener firma conjunta, para abrir la cuenta es necesario{" "}
                    <a
                      href={!isCryptoOnly ? "/ayuda" : undefined}
                      onClick={
                        isCryptoOnly
                          ? (e) => {
                              e.preventDefault();
                            }
                          : undefined
                      }
                      className="font-bold underline cursor-pointer"
                    >
                      contactar a soporte
                    </a>
                  </>
                ) : (
                  ""
                )}
              </div>
            </div>
          }
        >
          <span>
            <InfoIcon className={infoIcon} />
          </span>
        </Tooltip>
      </div>
    );
  };

  return (
    <>
      {isMobile && (
        <BackHeader
          onGoBack={onClose}
          companyName={companyName}
          showButton={!isCryptoOnly}
        />
      )}

      <ModalHeader className={`${isMobile && "pb-[0] px-[1rem]"}`}>
        {!isMobile ? (
          <span className={header}>{companyName}</span>
        ) : (
          <span className={description}>
            Seleccioná la cuenta para operar en {companyName}
          </span>
        )}
      </ModalHeader>
      <ModalBody
        className={`${flexContainer} ${isMobile && "py-[0] px-[1rem]"}`}
      >
        {!isMobile && (
          <span className={description}>
            Seleccioná la cuenta para operar en {companyName}
          </span>
        )}
        {!accountOptions || accountOptions?.length === 0 ? (
          <div className={spinnerContainer}>
            <Spinner />
          </div>
        ) : (
          <div className={scrollContent}>
            {accountOptions.map((account) => (
              <AccountCard
                key={account.number}
                legalName={account.legalName}
                isSelected={account.number === selectedAccount?.number}
                onClick={() => setSelectedAccount(account)}
                accountNumber={
                  isMobile ? account.number : `Comitente: ${account.number}`
                }
                isDisabled={
                  !account.acceptedTerms &&
                  (!account.isTitular || account.isJointSignature)
                }
                customBottomComponent={
                  account.acceptedTerms && (
                    <Chip
                      color="success"
                      radius="sm"
                      size="sm"
                      variant={
                        account.number === selectedAccount?.number
                          ? "solid"
                          : "flat"
                      }
                    >
                      Activa
                    </Chip>
                  )
                }
                customSideComponent={getCustomSideComponent(account)}
              />
            ))}
          </div>
        )}
        {!isMobile && (
          <span className={support}>
            ¿No encontrás tu cuenta?{" "}
            <a
              href={!isCryptoOnly ? "/ayuda" : undefined}
              onClick={
                isCryptoOnly
                  ? (e) => {
                      e.preventDefault();
                    }
                  : undefined
              }
            >
              Contactar a soporte
            </a>
          </span>
        )}
      </ModalBody>
      <ButtonsFooter
        isMobile={isMobile}
        primaryButton={{
          text: "Continuar",
          handleClick: () => {
            dispatch(updateCryptoSelectedAccount(selectedAccount?.number));
            dispatch(
              updateCryptoStep(
                selectedAccount?.acceptedTerms
                  ? !isMobile
                    ? "REDIRECT"
                    : "FEEDBACK"
                  : "DETAILS",
              ),
            );
          },
          isDisabled: !selectedAccount,
        }}
        {...(!isCryptoOnly && {
          secondaryButton: {
            text: !isMobile ? "Volver" : "Contactar a soporte",
            handleClick: !isMobile
              ? onClose
              : () => {
                  onClose();
                  navigate("/ayuda");
                },
          },
        })}
      />
    </>
  );
};

export default AccountsModal;
